/**
 * Lettura del file del programma (il JSON esportato dall'app dell'evento).
 *
 * Il database ricontrolla la versione, ma un file sbagliato a meta' lascerebbe
 * un programma zoppo: qui si controlla la forma prima di mandarlo, e si dice
 * in italiano cosa non va invece di mostrare un errore di Postgres.
 */

import type { AulaEvento, GiornoEvento, ProgrammaEvento, SessioneEvento, TurnoEvento } from '@eventi/tipi.ts';

export type EsitoFileProgramma =
  | { ok: true; programma: ProgrammaEvento }
  | { ok: false; errori: string[] };

const MAX_ERRORI = 8;
const DATA = /^\d{4}-\d{2}-\d{2}$/;

type Grezzo = Record<string, unknown>;

const oggetto = (v: unknown): v is Grezzo => typeof v === 'object' && v !== null && !Array.isArray(v);
const testo = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';
const intero = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v);

function leggiGiorni(grezzi: unknown[], errori: string[]): GiornoEvento[] {
  return grezzi.flatMap((g, i) => {
    if (!oggetto(g) || !testo(g.codice) || !testo(g.nome) || typeof g.data !== 'string' || !DATA.test(g.data)) {
      errori.push(`Giorno n. ${i + 1}: servono codice, nome e data (AAAA-MM-GG)`);
      return [];
    }
    return [{ codice: g.codice, data: g.data, nome: g.nome }];
  });
}

function leggiTurni(grezzi: unknown[], errori: string[]): TurnoEvento[] {
  return grezzi.flatMap((t, i) => {
    if (!oggetto(t) || !intero(t.n) || t.n < 1 || typeof t.inizio !== 'string' || typeof t.fine !== 'string') {
      errori.push(`Turno n. ${i + 1}: servono numero, inizio e fine`);
      return [];
    }
    return [{ n: t.n, inizio: t.inizio, fine: t.fine }];
  });
}

function leggiAule(grezze: unknown[], errori: string[]): AulaEvento[] {
  return grezze.flatMap((a, i) => {
    const posti = oggetto(a) ? a.posti : undefined;
    const postiValidi = posti === undefined || posti === null || (intero(posti) && posti >= 0);
    if (!oggetto(a) || !testo(a.codice) || !testo(a.nome) || !postiValidi) {
      errori.push(`Aula n. ${i + 1}: servono codice e nome; i posti, se ci sono, sono un numero intero`);
      return [];
    }
    return [{ codice: a.codice, nome: a.nome, posti: intero(posti) ? posti : null }];
  });
}

function leggiSessione(s: unknown, i: number, giorni: Set<string>, turni: Set<number>, aule: Set<string>, errori: string[]): SessioneEvento[] {
  const dove = oggetto(s) && testo(s.codice) ? `Sessione "${s.codice}"` : `Sessione n. ${i + 1}`;
  if (!oggetto(s) || !testo(s.codice) || !testo(s.titolo) || typeof s.inizio !== 'string' || typeof s.fine !== 'string') {
    errori.push(`${dove}: servono codice, titolo, inizio e fine`);
    return [];
  }
  if (typeof s.giorno !== 'string' || !giorni.has(s.giorno)) {
    errori.push(`${dove}: il giorno "${String(s.giorno)}" non c'e' fra i giorni del file`);
    return [];
  }
  if (typeof s.aula !== 'string' || !aule.has(s.aula)) {
    errori.push(`${dove}: l'aula "${String(s.aula)}" non c'e' fra le aule del file`);
    return [];
  }
  const turniSessione = Array.isArray(s.turni) ? s.turni : [];
  if (!turniSessione.every((n) => intero(n) && turni.has(n))) {
    errori.push(`${dove}: i turni devono essere fra quelli del file`);
    return [];
  }
  return [
    {
      codice: s.codice,
      giorno: s.giorno,
      turno: intero(s.turno) ? s.turno : null,
      turni: turniSessione as number[],
      inizio: s.inizio,
      fine: s.fine,
      aula: s.aula,
      titolo: s.titolo,
      relatori: typeof s.relatori === 'string' ? s.relatori : '',
      sceglibile: s.sceglibile === true,
    },
  ];
}

function doppioni(codici: string[]): string[] {
  const visti = new Set<string>();
  const doppi = new Set<string>();
  for (const c of codici) (visti.has(c) ? doppi : visti).add(c);
  return [...doppi];
}

/** Legge e controlla il testo del file. Restituisce il programma ripulito o l'elenco dei problemi. */
export function leggiFileProgramma(contenuto: string): EsitoFileProgramma {
  let dati: unknown;
  try {
    dati = JSON.parse(contenuto);
  } catch {
    return { ok: false, errori: ["Il file non si legge: deve essere il file .json esportato dall'app dell'evento."] };
  }
  if (!oggetto(dati)) return { ok: false, errori: ['Il file non contiene un programma.'] };
  if (dati.versione !== 1) {
    return { ok: false, errori: ["Versione del file non riconosciuta: esporta di nuovo il programma dall'app dell'evento."] };
  }
  for (const chiave of ['giorni', 'turni', 'aule', 'sessioni']) {
    if (!Array.isArray(dati[chiave]) || (dati[chiave] as unknown[]).length === 0) {
      return { ok: false, errori: [`Nel file manca l'elenco "${chiave}" (o e' vuoto).`] };
    }
  }

  const errori: string[] = [];
  const giorni = leggiGiorni(dati.giorni as unknown[], errori);
  const turni = leggiTurni(dati.turni as unknown[], errori);
  const aule = leggiAule(dati.aule as unknown[], errori);
  const codiciGiorni = new Set(giorni.map((g) => g.codice));
  const numeriTurni = new Set(turni.map((t) => t.n));
  const codiciAule = new Set(aule.map((a) => a.codice));
  const sessioni = (dati.sessioni as unknown[]).flatMap((s, i) =>
    leggiSessione(s, i, codiciGiorni, numeriTurni, codiciAule, errori),
  );

  for (const [cosa, codici] of [
    ['giorno', giorni.map((g) => g.codice)],
    ['turno', turni.map((t) => String(t.n))],
    ['aula', aule.map((a) => a.codice)],
    ['sessione', sessioni.map((s) => s.codice)],
  ] as const) {
    for (const c of doppioni([...codici])) errori.push(`Il codice ${cosa} "${c}" compare due volte`);
  }
  if (errori.length === 0 && !sessioni.some((s) => s.sceglibile)) {
    errori.push('Nessuna sessione e\' segnata come sceglibile: non ci sarebbe niente da scegliere.');
  }

  if (errori.length > 0) {
    const altri = errori.length - MAX_ERRORI;
    return { ok: false, errori: altri > 0 ? [...errori.slice(0, MAX_ERRORI), `…e altri ${altri} problemi`] : errori };
  }
  return { ok: true, programma: { versione: 1, giorni, turni, aule, sessioni } };
}
